import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, debounceTime, fromEvent, map, Observable, Subject} from "rxjs";
import {Socket} from "ngx-socket-io";
import {throttleTime} from "rxjs/operators";

interface TypingUser {
    userId: string;
    username: string;
}

@Injectable({
    providedIn: 'root'
})
export class SocketService implements OnDestroy {
    public newNotification: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public newBadge: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public newVotes: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public newComments: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public newPoints: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private onlineUsers: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    private typingUsers = new Map<number, BehaviorSubject<TypingUser[]>>();
    private typingStates = new Map<number, Subject<boolean>>();

    constructor(private socket: Socket) {
        this.socket.on('online-users-updated', (users: string[]): void => {
            this.onlineUsers.next(users);
        });
        this.socket.on('typing-users-updated', (data: { postId: string; users: TypingUser[] }) => {
            this.getOrCreateTypingSubject(data.postId).next(data.users);
        });
    }

    setNewNotification(data: any): void {
        this.newNotification.next(data);
    }

    getNewNotification(): Observable<any> {
        return this.newNotification.asObservable();
    }

    setNewBadge(data: any): void {
        this.newBadge.next(data);
    }

    getNewBadge(): Observable<any> {
        return this.newBadge.asObservable();
    }

    setNewPoints(data: any): void {
        this.newPoints.next(data);
    }

    getNewPoints(): Observable<any> {
        return this.newPoints.asObservable();
    }

    setNewComments(data: any): void {
        this.newComments.next(data);
    }

    getNewComments(): Observable<any> {
        return this.newComments.asObservable();
    }

    setNewVotes(data: any): void {
        this.newVotes.next(data);
    }

    getNewVotes(): Observable<any> {
        return this.newVotes.asObservable();
    }

    identify(userId: number, username: string): void {
        this.socket.emit('identify', {userId, username});
    }

    listenToEvents(): Observable<any> {
        return new Observable((observer): void => {
            this.socket.ioSocket.onAny((event: any, ...args: any[]): void => {
                observer.next(args);
            });
        });
    }

    isUserOnline(userId: number): Observable<boolean> {
        return new Observable(subscriber => {
            this.onlineUsers.subscribe(users => {
                subscriber.next(users.includes(String(userId)));
            });
        });
    }

    ngOnDestroy(): void {
        if (this.socket) {
            this.socket.disconnect();
        }
    }

    joinPost(postId: number): void {
        this.socket.emit('join-post', postId);
    }

    watchTyping(postId: number, inputElement: HTMLElement): void {
        fromEvent(inputElement, 'input').pipe(
            throttleTime(2000),
            map(() => true)
        ).subscribe(() => {
            this.socket.emit('typing-started', {postId});
        });

        fromEvent(inputElement, 'input').pipe(
            debounceTime(5000),
        ).subscribe(() => {
            this.socket.emit('typing-stopped', {postId});
        });
    }

    stopWatchingTyping(postId: number): void {
        const subject: Subject<boolean> | undefined = this.typingStates.get(postId);
        if (subject) {
            subject.next(false);
        }
    }

    getTypingUsers(postId: number): Observable<TypingUser[]> {
        return this.getOrCreateTypingSubject(postId).asObservable();
    }

    private getOrCreateTypingSubject(postId: string | number): BehaviorSubject<TypingUser[]> {
        if (typeof postId === 'string') {
            postId = Number(postId);
        }
        if (!this.typingUsers.has(postId)) {
            this.typingUsers.set(postId, new BehaviorSubject<TypingUser[]>([]));
        }
        return this.typingUsers.get(postId)!;
    }

    private getOrCreateTypingStateSubject(postId: number): Subject<boolean> {
        if (!this.typingStates.has(postId)) {
            this.typingStates.set(postId, new Subject<boolean>());
        }
        return this.typingStates.get(postId)!;
    }
}
